import {FC, FormEventHandler, MouseEventHandler, useCallback, useRef} from 'react';
import {styled} from 'styled-components';

import {Button} from '@shared-web/components/core/button';
import {Input} from '@shared-web/components/core/input';
import {useGlobalKeyPress} from '@shared-web/lib/use_global_key_press';
import {useMemoRef} from '@shared-web/lib/use_memo_ref';
import {useStringArrayQueryString} from '@shared-web/lib/use_query_string';
import {useStateRef} from '@shared-web/lib/use_state_ref';

import {ChampionCard} from '@src/components/champion_card';
import {CHAMPIONS} from '@src/lib/champions';
import {Team} from '@src/lib/teams';

interface ChampionsPoolProps {
  team: Team;
}

export const ChampionsPool: FC<ChampionsPoolProps> = props => {
  const {team} = props;
  const [selected, setSelected, selectedRef] = useStringArrayQueryString(team, []);

  // The filter textfield
  const filterInputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter, filterRef] = useStateRef('');
  const handleResetClick = useCallback(() => {
    setFilter('');
  }, [setFilter]);

  // Visible champions accounting for the filter
  const [visibleChampions, visibleChampionsRef] = useMemoRef(() => {
    const token = filter.trim().toUpperCase();
    const score = (label: string): number =>
      Math.max(
        ...label
          .toUpperCase()
          .split(' ')
          .map(str => {
            const i = str.indexOf(token);
            return i === -1 ? 0 : 1000 - i;
          })
      );
    return CHAMPIONS.map(champion => ({champion, score: score(champion.label)}))
      .filter(c => c.score > 0)
      .sort((c1, c2) =>
        c1.score === c2.score
          ? c1.champion.label.localeCompare(c2.champion.label)
          : c2.score - c1.score
      )
      .map(c => c.champion);
  }, [filter]);

  // Add/remove a champion to the team
  const toggleChampion = useCallback(
    (name: string) => {
      const isSelected = selected.includes(name);
      const curr = selectedRef.current;
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      const newCurr = isSelected ? curr.filter(l => l !== name) : [...curr.slice(0, 4), name];
      setSelected(newCurr);
      setFilter('');
      // Focus the input if we don't have a full team
      if (newCurr.length < 5) {
        filterInputRef.current?.focus();
      }
      // Blur the input if the team is full
      else {
        filterInputRef.current?.blur();
      }
    },
    [selected, selectedRef, setFilter, setSelected]
  );

  // Click on a champion in the pool
  const handleChampionClick = useCallback<MouseEventHandler>(
    evt => toggleChampion(evt.currentTarget.getAttribute('data-label') ?? ''),
    [toggleChampion]
  );

  // Automatically select the first champion visible (an not in the team) from the pool if the filter is active
  const selectFirstChampion = useCallback(() => {
    const isInputFocused = document.activeElement === filterInputRef.current;
    const isInputEmpty = filterRef.current.length === 0;
    const firstChampion = visibleChampionsRef.current.find(c => !selected.includes(c.label));
    if (isInputFocused && !isInputEmpty && firstChampion) {
      toggleChampion(firstChampion.label);
    }
  }, [filterRef, selected, toggleChampion, visibleChampionsRef]);

  // Form submit to auto-select
  const handleSubmit = useCallback<FormEventHandler>(
    evt => {
      evt.preventDefault();
      selectFirstChampion();
    },
    [selectFirstChampion]
  );
  useGlobalKeyPress(['Enter'], selectFirstChampion);

  return (
    <Wrapper>
      <Form onSubmit={handleSubmit} target="">
        <Input ref={filterInputRef} width={'100%'} value={filter} syncState={setFilter} />
        <Button onClick={handleResetClick}>RESET</Button>
      </Form>
      <PoolWrapper>
        {visibleChampions.map(champion => {
          return (
            <ChampionCard
              key={champion.label}
              team={team}
              data-label={champion.label}
              champion={champion}
              showHighlight
              showRemove
              onClick={handleChampionClick}
            />
          );
        })}
      </PoolWrapper>
    </Wrapper>
  );
};

ChampionsPool.displayName = 'ChampionsPool';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
`;

const PoolWrapper = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10vw, 1fr));
  user-select: none;
  gap: 8px;
  overflow-y: auto;
  padding: 16px;
`;

const Form = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 16px;
`;
